// Pure model for the multi-day booking grid (day columns × time rows).
import { dayKeyIn, dayPillLabels } from './bookingSlots.js';

/** @returns {number} minutes after local midnight of an instant in `timeZone`. */
export function minutesIn(iso, timeZone) {
  const parts = new Intl.DateTimeFormat('en-US', { timeZone, hour: '2-digit', minute: '2-digit', hour12: false }).formatToParts(new Date(iso));
  const get = (t) => Number(parts.find((p) => p.type === t)?.value || 0);
  return (get('hour') % 24) * 60 + get('minute');
}

/** "9:30 AM" for minutes after midnight. */
export function minutesLabel(total) {
  const h = Math.floor(total / 60);
  const m = total % 60;
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}${m ? `:${String(m).padStart(2, '0')}` : ''} ${h < 12 ? 'AM' : 'PM'}`;
}

/** YYYY-MM-DD plus n days (calendar arithmetic, no timezone involved). */
export function addDaysKey(key, n) {
  const d = new Date(`${key}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/**
 * @param {{slots: string[], timeZone?: string, stepMinutes?: number, daysOut?: number, now?: Date}} args
 * @returns {{columns: Array<{key: string, weekday: string, md: string, available: boolean}>,
 *   rows: Array<{minutes: number, label: string}>, cells: Map<string, string>}}
 *   cells maps `${dayKey}|${minutes}` to the slot's ISO instant; a missing key is "unavailable".
 */
export function buildGrid({ slots, timeZone, stepMinutes = 30, daysOut = 7, now = new Date() }) {
  const cells = new Map();
  const times = [];
  const lastDay = { key: '' };
  slots.forEach((s) => {
    const dk = dayKeyIn(s, timeZone);
    const mins = minutesIn(s, timeZone);
    cells.set(`${dk}|${mins}`, s);
    times.push(mins);
    if (dk > lastDay.key) lastDay.key = dk;
  });

  const first = dayKeyIn(now, timeZone);
  const span = Math.max(Math.min(Number(daysOut) || 7, 60), 1);
  let count = span;
  // Always cover every day that has a slot, even if the horizon is shorter.
  if (lastDay.key) {
    const needed = Math.round((new Date(`${lastDay.key}T12:00:00Z`) - new Date(`${first}T12:00:00Z`)) / 86400000) + 1;
    count = Math.max(count, needed);
  }
  const columns = Array.from({ length: count }, (_, i) => {
    const key = addDaysKey(first, i);
    const l = dayPillLabels(key);
    const [, m, d] = key.split('-').map(Number);
    return { key, weekday: l.weekday, md: `${m}/${d}`, available: [...cells.keys()].some((k) => k.startsWith(`${key}|`)) };
  });

  const rows = [];
  if (times.length) {
    const step = Math.max(5, Number(stepMinutes) || 30);
    for (let t = Math.floor(Math.min(...times) / step) * step; t <= Math.max(...times); t += step) rows.push({ minutes: t, label: minutesLabel(t) });
  }
  return { columns, rows, cells };
}
